/**
 * sentry.js — Frontend Sentry SDK initialization
 *
 * Sprint 22.7 Phase 1 framework upgrade, session 4 (2026-05-12).
 *
 * Imported as the FIRST line of main.jsx, before React or the
 * global fetch interceptor, so error capture sees React's lifecycle
 * hooks from the very start. The Vite-built bundle includes this
 * module only if VITE_SENTRY_DSN_FRONTEND was set at build time
 * (GitHub Actions repo secret, exposed to the build step via the
 * env: block in .github/workflows/deploy.yml); a build without the
 * DSN ships dead code that does nothing.
 *
 * Points at BetterStack's Sentry-compatible error-tracking endpoint
 * (same DSN target as backend per session-4 operator decision; see
 * docs/framework-briefs/2026-05-11-phase-1.md). The Vite plugin in
 * vite.config.js handles source map upload at build time so captured
 * stack traces show original source lines, not minified bundle
 * offsets.
 *
 * P17/P18: beforeBreadcrumb strips JWT from fetch breadcrumb
 * headers. The global fetch interceptor in main.jsx adds
 * Authorization for every /api/* request; without scrubbing, every
 * captured error would carry the user's token.
 */

import * as Sentry from '@sentry/react';

if (import.meta.env.VITE_SENTRY_DSN_FRONTEND) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN_FRONTEND,
    environment: import.meta.env.MODE, // 'development' or 'production'

    // No performance tracing or session replay for v1. Same
    // rationale as backend (instrument.js): stay well under the
    // free-tier budget until error volume is baselined. Sizing
    // flagged per Addendum 3 discipline.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    beforeBreadcrumb(breadcrumb) {
      // Strip Authorization from fetch breadcrumb headers. Every
      // /api/* call has it injected by the global fetch interceptor
      // in main.jsx; including it in captured errors would leak the
      // JWT into the error-tracking dashboard.
      if (breadcrumb.category === 'fetch' && breadcrumb.data?.headers) {
        delete breadcrumb.data.headers.Authorization;
        delete breadcrumb.data.headers.authorization;
      }
      return breadcrumb;
    },
  });
  console.log('[sentry] frontend init ok — DSN configured');
} else {
  // No-DSN path: the SDK is still importable for ErrorBoundary
  // wrapping in main.jsx, but never sends events. Quiet console
  // message helps operator confirm the bundle was built without DSN.
  console.log('[sentry] frontend SKIPPED — VITE_SENTRY_DSN_FRONTEND not set at build time');
}

export { Sentry };
